// The dictation link: one capture carried over one or more server sessions
// (C-4 dictation client rules, S1).
//
// - Frames are sent strictly in order. Frames captured before the socket is
//   ready wait in a backlog; nothing is ever dropped. Back-pressure pauses the
//   sender, and whenever more than one frame is waiting the sender is paced
//   at ≤ 4× real time (≥ 25 ms per frame).
// - On an unexpected close the link mints a NEW session with
//   `replayOf=<previous sessionId>` and replays the spooled frames from
//   floor(lastFinalEndMs × 16 / 1600), flagged REPLAY, then continues with the
//   backlog. It retries with back-off 0.5, 1, 2, 4, 8, 8 … s until 30 s have
//   passed since the close; then the capture is kept (`onFailure`). The finals
//   of earlier sessions are kept here and prepended to the committed text.
// - `commit(lastSeq)` sends every frame up to `lastSeq` (the last one flagged
//   FINAL_FRAME), then `stop`, and waits at most 10 s for `committed`. The
//   caller `hold()`s the link before it stops the capture, so the tail frame
//   the capture flushes is still queued when `commit` names it.
// - A close the server ends on purpose (C-4 close codes 4400/4408/4413/4429,
//   a fatal `error`, `closed{replaced}`) is terminal, not reconnected.
import { wsUrl } from '@/lib/shell';
import type {
  PcmFrame,
  SpeechMintRequest,
  SpeechMintResponse,
  SpeechSocketApi,
  SpeechSocketOptions,
} from '../contracts';
import { SpeechHttpError } from '../contracts';
import { speechIssue, speechIssueFor, terminalCloseCode, type SpeechIssue } from '../errors';
import {
  COMMIT_TIMEOUT_MS,
  FLAG_FINAL_FRAME,
  FLAG_REPLAY,
  RECONNECT_BACKOFF_MS,
  RECONNECT_WINDOW_MS,
  REPLAY_MIN_FRAME_INTERVAL_MS,
  replayFromSeq,
  type ServerMessage,
} from '../transport/protocol';

export type LinkTimers = {
  setTimeout(callback: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
};

export type LinkDeps = {
  mint(request: SpeechMintRequest): Promise<SpeechMintResponse>;
  openSocket(options: SpeechSocketOptions): SpeechSocketApi;
  /** Spooled frames in [from, to], in seq order. */
  frames(from: number, to: number): AsyncIterable<PcmFrame>;
  timers: LinkTimers;
  now(): number;
  online(): boolean;
  /** Subscribe to the browser coming back online; returns the unsubscribe. */
  onOnline(callback: () => void): () => void;
};

export type LinkEvents = {
  onMinted(mint: SpeechMintResponse): void;
  onReady(): void;
  onPartial(key: string, message: Extract<ServerMessage, { type: 'partial' }>): void;
  onFinal(key: string, message: Extract<ServerMessage, { type: 'final' }>): void;
  onDegraded(message: Extract<ServerMessage, { type: 'degraded' }>): void;
  /** The session closed unexpectedly and the link is re-minting. */
  onReconnecting(offline: boolean): void;
  /** The server committed without a `stop` (for example the budget ran out). */
  onUnsolicitedCommit(text: string): void;
  /** Terminal: the link gave up. The spool still holds the audio. */
  onFailure(issue: SpeechIssue): void;
};

/** Mint failures that another attempt cannot fix. */
// 402: the company's usage credit is used up (#2841); a retry cannot succeed until a card is added.
const TERMINAL_STATUSES: ReadonlySet<number> = new Set([400, 401, 402, 403, 415, 429]);
const BACKPRESSURE_RETRY_MS = 50;

const CLOSED_REASON_CODES: Record<string, string> = {
  idle: 'timeout',
  max_duration: 'too_long',
  replaced: 'replaced',
};

type Commit = { resolve(text: string): void; reject(issue: SpeechIssue): void };

export type LinkOptions = {
  /** Bound to the recording, including reconnects and kept-audio retries. */
  threadId?: string;
  /** Frames already in the spool when the link starts (Retry): replayed first. */
  capturedThrough?: number;
};

export class DictationLink {
  private sessionId: string | null = null;
  private minted: SpeechMintResponse | null = null;
  private socket: SpeechSocketApi | null = null;
  private generation = 0;
  private sessions = 0;
  private ready = false;
  private done = false;
  private nextSeq = 0;
  private lastCaptured = -1;
  private backlog: PcmFrame[] = [];
  private replay: { from: number; to: number } | null = null;
  private finalSeq: number | null = null;
  private stopSent = false;
  /** Live frames wait in the backlog until `commit` names the last one. */
  private holding = false;
  private lastFinalEndMs = 0;
  private earlierText: string[] = [];
  private currentText: string[] = [];
  private fatalCode: string | null = null;
  private closedReason: string | null = null;
  private lastFailure: string | null = null;
  private closeStart: number | null = null;
  private attempt = 0;
  private retryTimer: unknown = null;
  private commitTimer: unknown = null;
  private unsubscribeOnline: (() => void) | null = null;
  private commitWaiter: Commit | null = null;
  private lastSendAt = Number.NEGATIVE_INFINITY;
  private pumping = false;

  constructor(
    private readonly options: LinkOptions,
    private readonly deps: LinkDeps,
    private readonly events: LinkEvents,
  ) {
    if (options.capturedThrough !== undefined) this.lastCaptured = options.capturedThrough;
  }

  /** The current server session, for diagnostics and `replayOf`. */
  get serverSessionId(): string | null {
    return this.sessionId;
  }

  start(): void {
    void this.connect();
  }

  /** A live frame from the capture; the spool already holds it. */
  push(frame: PcmFrame): void {
    if (this.done || frame.seq <= this.lastCaptured) return;
    this.lastCaptured = frame.seq;
    this.backlog.push(frame);
    void this.pump();
  }

  /**
   * The capture is ending: keep new live frames in the backlog until `commit`
   * says which one is last, so that one goes out flagged FINAL_FRAME.
   */
  hold(): void {
    this.holding = true;
  }

  /** Send everything through `lastSeq`, then `stop`; resolves with the whole dictated text. */
  commit(lastSeq: number): Promise<string> {
    if (this.done) return Promise.reject(speechIssue('internal'));
    this.finalSeq = lastSeq;
    this.holding = false;
    const committed = new Promise<string>((resolve, reject) => {
      this.commitWaiter = { resolve, reject };
    });
    void this.pump();
    return committed;
  }

  /** Drop the audio server-side and close. */
  cancel(): void {
    if (this.done) return;
    this.socket?.send({ type: 'cancel' });
    this.shutdown();
  }

  /** Close without telling the server anything more (the page is going away). */
  close(): void {
    if (!this.done) this.shutdown();
  }

  // ── Connection ────────────────────────────────────────────────────────────

  private async connect() {
    if (this.done) return;
    const generation = ++this.generation;
    let minted: SpeechMintResponse;
    try {
      minted = await this.deps.mint({
        mode: 'dictation',
        ...(this.options.threadId ? { threadId: this.options.threadId } : {}),
        ...(this.sessionId ? { replayOf: this.sessionId } : {}),
      });
    } catch (error) {
      if (generation === this.generation && !this.done) this.mintFailed(error);
      return;
    }
    if (generation !== this.generation || this.done) return;
    this.minted = minted;
    this.events.onMinted(minted);
    this.fatalCode = null;
    this.closedReason = null;
    this.socket = this.deps.openSocket({
      url: `${wsUrl(minted.wsPath)}?ticket=${encodeURIComponent(minted.ticket)}`,
      onMessage: (message) => {
        if (generation === this.generation) this.onMessage(message);
      },
      onClose: (code) => {
        if (generation === this.generation) this.onClose(code);
      },
    });
  }

  private mintFailed(error: unknown) {
    const issue = speechIssueFor(error);
    const terminal =
      (error instanceof SpeechHttpError && TERMINAL_STATUSES.has(error.status)) ||
      issue.code === 'not_configured';
    if (terminal) return this.fail(issue);
    this.lastFailure = issue.code;
    this.scheduleRetry(error instanceof SpeechHttpError ? error.retryAfterSec : null);
  }

  private scheduleRetry(retryAfterSec: number | null) {
    if (this.done) return;
    this.closeStart ??= this.deps.now();
    const elapsed = this.deps.now() - this.closeStart;
    const remaining = RECONNECT_WINDOW_MS - elapsed;
    if (remaining <= 0) return this.giveUp();
    const offline = !this.deps.online();
    this.events.onReconnecting(offline);
    const backoff =
      RECONNECT_BACKOFF_MS[Math.min(this.attempt, RECONNECT_BACKOFF_MS.length - 1)] ?? 8_000;
    this.attempt += 1;
    const delay = Math.min(remaining, Math.max(backoff, (retryAfterSec ?? 0) * 1000));
    this.clearRetry();
    this.retryTimer = this.deps.timers.setTimeout(() => this.retryNow(), delay);
    if (offline && !this.unsubscribeOnline)
      this.unsubscribeOnline = this.deps.onOnline(() => this.retryNow());
  }

  private retryNow() {
    this.clearRetry();
    if (this.done) return;
    if (this.closeStart !== null && this.deps.now() - this.closeStart >= RECONNECT_WINDOW_MS)
      return this.giveUp();
    void this.connect();
  }

  private giveUp() {
    const code = this.deps.online() ? (this.lastFailure ?? 'timeout') : 'offline';
    this.fail(speechIssue(code === 'request_failed' ? 'offline' : code));
  }

  // ── Server messages (table-driven) ──────────────────────────────────────

  private readonly handlers: {
    [K in ServerMessage['type']]: (message: Extract<ServerMessage, { type: K }>) => void;
  } = {
    ready: () => this.onReady(),
    ack: () => {},
    partial: (message) => this.events.onPartial(this.segmentKey(message.seg), message),
    final: (message) => {
      if (message.text.trim()) this.currentText.push(message.text.trim());
      this.lastFinalEndMs = Math.max(this.lastFinalEndMs, message.endMs);
      this.events.onFinal(this.segmentKey(message.seg), message);
    },
    committed: (message) => this.onCommitted(message.text),
    resend: (message) => {
      if (message.fromSeq >= this.nextSeq) return;
      this.replay = { from: message.fromSeq, to: this.nextSeq - 1 };
      this.nextSeq = message.fromSeq;
      void this.pump();
    },
    degraded: (message) => this.events.onDegraded(message),
    error: (message) => {
      if (message.fatal) this.fatalCode = message.code;
    },
    closed: (message) => {
      this.closedReason = message.reason;
    },
    // Voice sessions only (BUILD 2 C2-4); a dictation session never gets one.
    endpoint: () => {},
  };

  private onMessage(message: ServerMessage) {
    if (this.done) return;
    (this.handlers[message.type] as (m: ServerMessage) => void)(message);
  }

  private segmentKey(seg: number | string) {
    return `${this.sessions}:${seg}`;
  }

  private onReady() {
    if (this.minted) this.sessionId = this.minted.sessionId;
    this.sessions += 1;
    this.ready = true;
    this.closeStart = null;
    this.attempt = 0;
    this.lastFailure = null;
    this.unsubscribeOnline?.();
    this.unsubscribeOnline = null;
    // Everything before the backlog was sent to an earlier session (or was
    // captured before a Retry): replay it from the spool.
    const from = replayFromSeq(this.lastFinalEndMs);
    const firstUnsent = this.backlog[0]?.seq ?? this.lastCaptured + 1;
    this.replay = from < firstUnsent ? { from, to: firstUnsent - 1 } : null;
    this.nextSeq = from;
    this.events.onReady();
    void this.pump();
  }

  private onCommitted(text: string) {
    const whole = [...this.earlierText, text.trim()].filter(Boolean).join(' ');
    const waiter = this.commitWaiter;
    this.commitWaiter = null;
    this.shutdown();
    if (waiter) waiter.resolve(whole);
    else this.events.onUnsolicitedCommit(whole);
  }

  private onClose(code: number) {
    this.ready = false;
    this.socket = null;
    if (this.done) return;
    const terminal =
      this.fatalCode ??
      terminalCloseCode(code) ??
      (this.closedReason ? (CLOSED_REASON_CODES[this.closedReason] ?? null) : null);
    if (terminal) return this.fail(speechIssue(terminal));
    // Unexpected: keep this session's finals and replay from the last one.
    this.earlierText.push(...this.currentText);
    this.currentText = [];
    this.stopSent = false;
    this.clearCommitTimer();
    this.scheduleRetry(null);
  }

  // ── Sending ──────────────────────────────────────────────────────────────

  private async pump() {
    if (this.pumping || !this.ready || this.done) return;
    this.pumping = true;
    const generation = this.generation;
    try {
      while (this.ready && generation === this.generation && !this.done) {
        if (this.replay) {
          await this.sendReplay(generation);
          continue;
        }
        const frame = this.backlog[0];
        if (!frame || this.holding) break;
        if (frame.seq < this.nextSeq) {
          this.backlog.shift();
          continue;
        }
        if (frame.seq > this.nextSeq) {
          this.replay = { from: this.nextSeq, to: frame.seq - 1 };
          continue;
        }
        if (await this.send(frame, 0, generation, this.backlog.length > 1)) this.backlog.shift();
      }
      if (generation === this.generation) this.maybeStop();
    } finally {
      this.pumping = false;
    }
    // A frame or a commit may have arrived while the loop was finishing.
    if (this.ready && !this.done && ((this.backlog.length > 0 && !this.holding) || this.replay))
      void this.pump();
  }

  private async sendReplay(generation: number) {
    const replay = this.replay!;
    for await (const frame of this.deps.frames(replay.from, replay.to)) {
      if (generation !== this.generation || !this.ready || this.done || this.replay !== replay)
        return;
      if (frame.seq < this.nextSeq) continue;
      // A frame the spool no longer has cannot be recovered; move past it.
      if (frame.seq > this.nextSeq) this.nextSeq = frame.seq;
      if (!(await this.send(frame, FLAG_REPLAY, generation, true))) return;
    }
    if (this.replay === replay) {
      this.nextSeq = Math.max(this.nextSeq, replay.to + 1);
      this.replay = null;
    }
  }

  /** True when sent; false when the session changed or this frame is no longer next. */
  private async send(frame: PcmFrame, flags: number, generation: number, paced: boolean) {
    for (;;) {
      if (paced) await this.waitPace();
      if (generation !== this.generation || !this.ready || this.done) return false;
      if (frame.seq !== this.nextSeq) return false;
      const final = frame.seq === this.finalSeq ? FLAG_FINAL_FRAME : 0;
      if (this.socket?.sendFrame(frame, flags | final)) {
        this.lastSendAt = this.deps.now();
        this.nextSeq = frame.seq + 1;
        return true;
      }
      await this.sleep(BACKPRESSURE_RETRY_MS);
    }
  }

  private waitPace(): Promise<void> {
    const wait = this.lastSendAt + REPLAY_MIN_FRAME_INTERVAL_MS - this.deps.now();
    return wait > 0 ? this.sleep(wait) : Promise.resolve();
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => this.deps.timers.setTimeout(resolve, ms));
  }

  private maybeStop() {
    if (this.finalSeq === null || this.stopSent || !this.ready || this.replay) return;
    if (this.nextSeq <= this.finalSeq || this.backlog.length > 0) return;
    this.socket?.send({ type: 'stop', lastSeq: this.finalSeq });
    this.stopSent = true;
    this.clearCommitTimer();
    this.commitTimer = this.deps.timers.setTimeout(
      () => this.fail(speechIssue('timeout')),
      COMMIT_TIMEOUT_MS,
    );
  }

  // ── Endings ──────────────────────────────────────────────────────────────

  private fail(issue: SpeechIssue) {
    if (this.done) return;
    const waiter = this.commitWaiter;
    this.commitWaiter = null;
    this.shutdown();
    waiter?.reject(issue);
    this.events.onFailure(issue);
  }

  private shutdown() {
    this.done = true;
    this.ready = false;
    this.clearRetry();
    this.clearCommitTimer();
    this.unsubscribeOnline?.();
    this.unsubscribeOnline = null;
    const socket = this.socket;
    this.socket = null;
    this.generation += 1;
    socket?.close();
  }

  private clearRetry() {
    if (this.retryTimer !== null) this.deps.timers.clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private clearCommitTimer() {
    if (this.commitTimer !== null) this.deps.timers.clearTimeout(this.commitTimer);
    this.commitTimer = null;
  }
}
